import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
} from "@mui/material";
import { useRouter } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { useInView } from "react-intersection-observer";

import type { ProductCardSortBy } from "@/types/ingestedProduct";

import {
  PRODUCT_CARD_DEFAULT_SORT_LABEL,
  PRODUCT_CARD_SORT_BY_LABEL,
  PRODUCT_CARD_SORT_BY_VALUES,
} from "@/features/SKUStore/collections/productCardListOptions";
import {
  searchForCollection,
  type SKUStoreCollection,
} from "@/features/SKUStore/collections/skuStoreCollections";
import { useCollectionProductCards } from "@/features/SKUStore/collections/useCollectionProductCards";
import { SKUStoreProductCard } from "@/features/SKUStore/components/ResourceCard/SKUStoreProductCard";

const cardGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(auto-fill, minmax(240px, 1fr))",
  },
  gap: 3,
  maxWidth: "100%",
} as const;

/** Sentinel select value for "no server sort" (catalog order). */
const DEFAULT_SORT_VALUE = "";

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body1" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  );
}

type ProductSortSelectProps = {
  sortBy: ProductCardSortBy | undefined;
  onChange: (next: ProductCardSortBy | undefined) => void;
};

/**
 * Server-side sort (the grid is infinite scroll), so every change refetches
 * from page 1.
 */
function ProductSortSelect({ sortBy, onChange }: ProductSortSelectProps) {
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel id="sku-store-product-sort-label">Sort by</InputLabel>
      <Select
        labelId="sku-store-product-sort-label"
        id="sku-store-product-sort"
        label="Sort by"
        value={sortBy ?? DEFAULT_SORT_VALUE}
        onChange={(event: SelectChangeEvent<string>) => {
          const value = event.target.value;
          onChange(
            value === DEFAULT_SORT_VALUE
              ? undefined
              : (value as ProductCardSortBy),
          );
        }}
      >
        <MenuItem value={DEFAULT_SORT_VALUE}>
          {PRODUCT_CARD_DEFAULT_SORT_LABEL}
        </MenuItem>
        {PRODUCT_CARD_SORT_BY_VALUES.map((value) => (
          <MenuItem key={value} value={value}>
            {PRODUCT_CARD_SORT_BY_LABEL[value]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export type SKUStoreCollectionProductsTabProps = {
  siteId: string;
  collection: SKUStoreCollection;
  /** Sort state, owned by the landing route's URL search params. */
  sortBy?: ProductCardSortBy;
  onSortByChange: (next: ProductCardSortBy | undefined) => void;
};

export function SKUStoreCollectionProductsTab({
  siteId,
  collection,
  sortBy,
  onSortByChange,
}: SKUStoreCollectionProductsTabProps) {
  const router = useRouter();

  const {
    data: infiniteData,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useCollectionProductCards(siteId, collection, sortBy);

  const cards = infiniteData?.pages.flatMap((page) => page.products) ?? [];
  const totalCount = infiniteData?.pages[0]?.count ?? cards.length;

  // Load the next page when the sentinel scrolls into view.
  const { ref: loadMoreRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Stack spacing={5} sx={{ px: 3, pt: 4, pb: 5, minWidth: 0 }}>
      <Stack spacing={3}>
        <Stack
          direction="row"
          useFlexGap
          sx={{
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          <SectionHeading>Products ({totalCount})</SectionHeading>
          <ProductSortSelect sortBy={sortBy} onChange={onSortByChange} />
        </Stack>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Typography variant="body2" color="error">
            Could not load products.
          </Typography>
        ) : cards.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No products in {collection.name} yet.
          </Typography>
        ) : (
          <>
            <Box sx={cardGridSx}>
              {cards.map((card) => (
                <SKUStoreProductCard
                  key={card.product_id}
                  card={card}
                  onEdit={
                    card.product_id
                      ? () => {
                          // Editing opens in a new tab so the grid stays put.
                          const { href } = router.buildLocation({
                            to: "/sku-store/$siteId/$skuId",
                            params: { siteId, skuId: card.product_id },
                            search: searchForCollection(collection),
                          });
                          window.open(href, "_blank", "noopener,noreferrer");
                        }
                      : undefined
                  }
                  onOpenVariants={
                    card.product_id
                      ? () => {
                          // Same as Edit: a new tab, so the grid stays put.
                          const { href } = router.buildLocation({
                            to: "/sku-store/$siteId/$skuId/variants",
                            params: { siteId, skuId: card.product_id },
                            search: searchForCollection(collection),
                          });
                          window.open(href, "_blank", "noopener,noreferrer");
                        }
                      : undefined
                  }
                />
              ))}
            </Box>
            {hasNextPage && (
              <Box
                ref={loadMoreRef}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 3,
                  minHeight: 40,
                }}
              >
                {isFetchingNextPage && <CircularProgress size={24} />}
              </Box>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
}
